"use client";

import {useForm} from "react-hook-form";
import {signIn} from "next-auth/react";
import {useRouter, useSearchParams} from "next/navigation";
import {useRequest} from "@/elements/hooks/useRequest";
import {Loader} from "@/elements/components/loader";
import {Button} from "@/elements/primitives/button";
import {Input} from "@/elements/primitives/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/elements/primitives/form";
import {getTemporaryItem, setTemporaryItem} from "@/lib/storage";

export default function Module() {
  const router = useRouter();
  const params = useSearchParams();

  const {request, loading} = useRequest(async (email: string) => {
    return await signIn("email", {
      email,
      redirect: false,
      callbackUrl: params.get("callbackUrl") ?? "/",
    });
  });

  const form = useForm({
    defaultValues: {
      email: getTemporaryItem("login:email") ?? "",
    },
  });

  async function actionSubmit({email}: {email: string}) {
    try {
      setTemporaryItem("login:email", email);

      const result = await request(email);

      if (result?.ok && !result.error) {
        router.push("/login/success");
        return;
      }

      router.push(`/login/error?error=${result?.error ?? "Default"}`);
    } catch {
      router.push("/login/error");
    }
  }

  return (
    <div className="w-full max-w-120 px-10 py-8 rounded-xl bg-white shadow-[0px_8px_64px_rgba(0,0,0,0.05)]">
      <div className="mb-1 text-center text-[22px] font-bold">Funnel</div>

      <div className="mb-5 text-center text-primary/70 text-[13px]">
        Start building funnels in minutes
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(actionSubmit)}
          className="leading-[1.35]"
        >
          <FormField
            rules={{
              required: "Email is required",
            }}
            control={form.control}
            name="email"
            render={({field}) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="email@example.com"
                    className="py-3.5 px-3.5"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            variant="primary"
            className="w-full mt-3 mb-5 justify-center py-[13.5px] px-4"
            disabled={loading}
          >
            {loading ? (
              <Loader className="size-5 [&>svg]:text-white/50 [&>svg]:fill-white [&>svg]:m-0!" />
            ) : (
              "Sign in"
            )}
          </Button>

          <FormDescription className="text-center text-[11px]">
            You will receive email with authentication button
          </FormDescription>
        </form>
      </Form>
    </div>
  );
}
